import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import DataTable from "components/QuestDataTable";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { hasPermission, mergeGrantedPermissions } from "utils/granularPermissions";
import { apiErrorMessage, apiSuccessMessage, readJsonSafely } from "utils/apiResponseMessages";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RestoreIcon from "@mui/icons-material/Restore";

const TrashedUserList = () => {
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [restoreDialog, setRestoreDialog] = useState({ open: false, user: null, loading: false });
  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => mergeGrantedPermissions(permissions, userPermission),
    [permissions, userPermission]
  );

  const hasUsersPermission = useCallback(
    (...requiredPermissions) => hasPermission(grantedPermissions, ...requiredPermissions),
    [grantedPermissions]
  );

  const canViewPage = hasUsersPermission("users.pages.trashed.view");
  const canCreate =
    hasUsersPermission("users.pages.create.view") &&
    hasUsersPermission("users.actions.open_create");
  const canOpenList =
    hasUsersPermission("users.pages.list.view") &&
    hasUsersPermission("users.actions.open_list");
  const canRestore = hasUsersPermission("users.actions.restore");
  const canConfirmRestore = hasUsersPermission("users.actions.confirm_restore");
  const canCancelRestore = hasUsersPermission("users.actions.cancel_restore");
  const canViewField = useCallback(
    (field) => hasUsersPermission(`users.fields.${field}.view`),
    [hasUsersPermission]
  );

  const fetchTrashedUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/users/trashed/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readJsonSafely(res);

      if (!res.ok) {
        throw new Error(apiErrorMessage(data, "Failed to fetch trashed users"));
      }

      setUsers(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Error loading trashed users",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchTrashedUsers();
  }, [canViewPage, fetchTrashedUsers, isAuthReady]);

  const handleRestoreClick = (user) => {
    if (!canRestore) {
      setSnackbar({ open: true, message: "You do not have permission to restore users", color: "error" });
      return;
    }

    setRestoreDialog({ open: true, user, loading: false });
  };

  const handleRestoreConfirm = async () => {
    if (!canRestore || !canConfirmRestore) {
      setSnackbar({ open: true, message: "You do not have permission to confirm restore", color: "error" });
      return;
    }

    try {
      setRestoreDialog((prev) => ({ ...prev, loading: true }));
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/users/restore/${restoreDialog.user?.id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await readJsonSafely(res);

      if (!res.ok) {
        throw new Error(apiErrorMessage(data, "Restore failed"));
      }

      setSnackbar({
        open: true,
        message: apiSuccessMessage(data, "User restored successfully"),
        color: "success",
      });
      await fetchTrashedUsers();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Restore failed", color: "error" });
    } finally {
      setRestoreDialog({ open: false, user: null, loading: false });
    }
  };

  const closeRestoreDialog = () => {
    if (!canCancelRestore || restoreDialog.loading) return;
    setRestoreDialog({ open: false, user: null, loading: false });
  };

  const columns = [];
  if (canViewField("id")) columns.push({ Header: "ID", accessor: "id", align: "left" });
  if (canViewField("name")) columns.push({ Header: "Name", accessor: "name", align: "left" });
  if (canViewField("email")) columns.push({ Header: "Email", accessor: "email", align: "left" });
  if (canViewField("branch")) columns.push({ Header: "Branch", accessor: "branch", align: "left" });
  if (canViewField("sub_branch")) columns.push({ Header: "Sub Branch", accessor: "subbranch", align: "left" });
  if (canViewField("user_code")) columns.push({ Header: "Emp Code", accessor: "user_code", align: "left" });
  if (canViewField("role")) columns.push({ Header: "Role", accessor: "role", align: "left" });
  if (canViewField("deleted_at")) columns.push({ Header: "Deleted At", accessor: "deleted_at", align: "left" });
  if (canViewField("actions") && canRestore) {
    columns.push({ Header: "Actions", accessor: "actions", align: "center", disableSortBy: true });
  }

  const rows = users.map((user) => ({
    id: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.id}
      </SoftTypography>
    ),
    name: (
      <SoftTypography variant="button" fontWeight="medium">
        {`${user.name || ""} ${user.last_name || ""}`.trim() || "-"}
      </SoftTypography>
    ),
    email: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.email || "-"}
      </SoftTypography>
    ),
    branch: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.branch?.name || "-"}
      </SoftTypography>
    ),
    subbranch: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.sub_branch?.name || user.subbranch?.name || "-"}
      </SoftTypography>
    ),
    user_code: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.user_code || "-"}
      </SoftTypography>
    ),
    role: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.custom_role || user.role || "-"}
      </SoftTypography>
    ),
    deleted_at: (
      <SoftTypography variant="button" fontWeight="medium">
        {user.deleted_at ? new Date(user.deleted_at).toLocaleString() : "-"}
      </SoftTypography>
    ),
    actions: (
      <SoftBox display="flex" justifyContent="center">
        {canRestore && (
          <Tooltip title="Restore User">
            <IconButton
              size="small"
              color="success"
              onClick={() => handleRestoreClick(user)}
              aria-label="restore"
              disabled={restoreDialog.loading && restoreDialog.user?.id === user.id}
            >
              {restoreDialog.loading && restoreDialog.user?.id === user.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RestoreIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </SoftBox>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="body2">Loading...</SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="medium">
            Trashed User List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view trashed users.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="User"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Trashed User List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/users/add")}
              >
                Add New User
              </SoftButton>
            )}
            {canOpenList && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>list</Icon>}
                onClick={() => navigate("/users/list")}
              >
                User List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        {users.length === 0 ? (
          <SoftBox textAlign="center" py={4}>
            <SoftTypography variant="body2">No trashed users found</SoftTypography>
          </SoftBox>
        ) : null}

        <Card style={{ flex: 1, borderRadius: "0px" }}>
          <DataTable
            table={{
              columns,
              rows,
            }}
            entriesPerPage={false}
          />
        </Card>
      </SoftBox>

      <Dialog open={restoreDialog.open} onClose={closeRestoreDialog}>
        <DialogTitle>Confirm Restore</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to restore the user{" "}
            <strong>
              {`${restoreDialog.user?.name || ""} ${restoreDialog.user?.last_name || ""}`.trim() || "this user"}
            </strong>
            ?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmRestore && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleRestoreConfirm}
              disabled={restoreDialog.loading}
              endIcon={restoreDialog.loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {restoreDialog.loading ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelRestore && (
            <SoftButton
              variant="contained"
              color="error"
              onClick={closeRestoreDialog}
              disabled={restoreDialog.loading}
            >
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default TrashedUserList;
